import React, { useCallback, useMemo, useState } from "react";
import FloatingPopover, {
  FLOATING_POPOVER_MODE,
  FLOATING_POPOVER_PLACEMENT,
} from "components/common/FloatingPopover";
import cosApi from "store/services/cos.services";
import ahApi from "store/services/ah.services";
import { stripHtmlToPlainText, textTruncate } from "Utils/utils";
import {
  getCosCaseNoteListPreviewText,
  hasCosCaseNoteListBody,
} from "pages/SupportCoordination/Participant/ParticipantProfile/CaseNotes/Submitted/cosCaseNoteDisplayUtils";

const notesCache = new Map();

const getHistoryNotePreview = (row) => {
  if (!row) return "";
  const fromList = getCosCaseNoteListPreviewText(row);
  if (fromList) return fromList;
  return stripHtmlToPlainText(row.notes);
};

const CaseNoteHistoryNoteCell = ({
  row,
  caseNoteApiModule = "cos",
  displayMaxLength = 120,
  className = "line-clamp-2 break-words leading-snug",
}) => {
  const [fullNotes, setFullNotes] = useState(() =>
    row?.uuid ? notesCache.get(row.uuid) ?? null : null
  );
  const [loading, setLoading] = useState(false);

  const previewText = useMemo(() => getHistoryNotePreview(row), [row]);
  const displayText = previewText
    ? textTruncate(previewText, displayMaxLength, displayMaxLength - 1)
    : "—";
  const needsFetch = row?.has_more_notes === true;
  const isTruncatedInCell =
    previewText.length > displayMaxLength || displayText.endsWith("...");
  const hasHoverContent =
    hasCosCaseNoteListBody(row) && (needsFetch || isTruncatedInCell);

  const loadFullNotes = useCallback(async () => {
    if (!needsFetch || fullNotes || loading) return;

    const cached = row?.uuid ? notesCache.get(row.uuid) : null;
    if (cached) {
      setFullNotes(cached);
      return;
    }

    const ptUuid = row?.participant?.uuid;
    if (!ptUuid || !row?.uuid) return;

    setLoading(true);
    try {
      const api = caseNoteApiModule === "ah" ? ahApi : cosApi;
      const response = await api.getCaseNotesDetails({
        uuid: row.uuid,
        pt_id: ptUuid,
      });
      const notes = response?.data?.notes ?? null;
      if (row.uuid && notes) {
        notesCache.set(row.uuid, notes);
      }
      setFullNotes(notes);
    } catch (error) {
      console.error("Failed to load case note on hover:", error);
    } finally {
      setLoading(false);
    }
  }, [caseNoteApiModule, fullNotes, loading, needsFetch, row]);

  const tooltipText = useMemo(() => {
    if (loading) return "Loading…";
    if (fullNotes) return stripHtmlToPlainText(fullNotes) || "—";
    return previewText || "—";
  }, [fullNotes, loading, previewText]);

  const handleOpenChange = useCallback(
    (open) => {
      if (open) loadFullNotes();
    },
    [loadFullNotes]
  );

  if (!hasHoverContent) {
    return <div className={className}>{displayText}</div>;
  }

  return (
    <FloatingPopover
      mode={FLOATING_POPOVER_MODE.TOOLTIP}
      placement={FLOATING_POPOVER_PLACEMENT.TOP}
      maxWidth={420}
      openDelay={250}
      onOpenChange={handleOpenChange}
      content={
        <div className='max-h-64 overflow-y-auto whitespace-pre-wrap break-words p-2 text-xs leading-relaxed text-slate-800'>
          {tooltipText}
        </div>
      }
    >
      <div className={`${className} cursor-help`}>{displayText}</div>
    </FloatingPopover>
  );
};

export default CaseNoteHistoryNoteCell;
